import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTheme } from '../context/ThemeContext';
import { HexLockup } from './FieldCertLogo';
import api from '../lib/api';
import queryClient from '../lib/queryClient';
import { isSuperAdmin } from '../lib/roles';
import { useMediaQuery, MOBILE_BREAKPOINT } from '../hooks/useMediaQuery';
import {
  FiMenu,
  FiX,
  FiGrid,
  FiMapPin,
  FiBarChart2,
  FiSettings,
  FiFileText,
  FiLogOut,
  FiGlobe,
  FiShield,
  FiLayers,
} from 'react-icons/fi';

function Layout() {
  const { user, logoutLocal } = useAuthStore();
  const { effectiveTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isTestsPage =
    location.pathname === '/tests' ||
    location.pathname.startsWith('/tests/') ||
    location.pathname === '/repairs';
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const showMobileTabs = isMobile && !isTestsPage;
  const hideAppHeader = isTestsPage && isMobile;
  const path = location.pathname;
  const isDashboardHome = path === '/' || path === '';
  const hideMobileHeaderBrand = isMobile && isDashboardHome;

  const handleLogout = async () => {
    setSidebarOpen(false);
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    logoutLocal();
    // Drop personalized caches (e.g. is_mine on in-progress sessions) so the next
    // login never briefly sees the previous user's ownership flags.
    queryClient.clear();
    navigate('/login');
  };

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const settingsAreaActive = path.startsWith('/settings');
  const settingsRoute = isAdmin ? '/settings/organization' : '/settings/my-settings';

  const mobileTabClass = ({ isActive }) =>
    `layout-mobile-tab ${isActive ? 'layout-mobile-tab-active' : ''}`;

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className={`layout ${isTestsPage ? 'layout-hide-sidebar-desktop' : ''}`}>
      {!hideAppHeader && !showMobileTabs && (
        <header className="layout-mobile-header" aria-label="Main navigation">
          <button
            type="button"
            className="layout-hamburger"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <FiMenu size={24} aria-hidden />
          </button>
          {!hideMobileHeaderBrand && (
            <HexLockup iconSize={28} wmSize="xs" gap={8} light={effectiveTheme === 'light'} />
          )}
        </header>
      )}

      {!showMobileTabs && (
        <>
          <div
            className={`layout-backdrop ${sidebarOpen ? 'layout-backdrop-visible' : ''}`}
            onClick={closeSidebar}
            onKeyDown={(e) => e.key === 'Escape' && closeSidebar()}
            role="button"
            tabIndex={-1}
            aria-hidden
          />

          <aside className={`layout-sidebar ${sidebarOpen ? 'layout-sidebar-open' : ''}`}>
            <div className="layout-sidebar-inner">
              <div className="layout-brand-row">
                <HexLockup iconSize={36} wmSize="sm" gap={12} surface="dark" />
                <button
                  type="button"
                  className="layout-sidebar-close"
                  onClick={closeSidebar}
                  aria-label="Close menu"
                >
                  <FiX size={22} aria-hidden />
                </button>
              </div>
              <nav className="layout-nav" onClick={closeSidebar}>
                <div className="layout-nav-section-label" aria-hidden>
                  Workspace
                </div>
                <NavLink to="/" end className={({ isActive }) =>
                  `layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`
                }>
                  <FiGrid size={20} className="layout-nav-icon" aria-hidden />
                  <span>Dashboard</span>
                </NavLink>
                <NavLink to="/sites" className={({ isActive }) =>
                  `layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`
                }>
                  <FiMapPin size={20} className="layout-nav-icon" aria-hidden />
                  <span>Sites</span>
                </NavLink>
                <NavLink to="/reports" className={({ isActive }) =>
                  `layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`
                }>
                  <FiBarChart2 size={20} className="layout-nav-icon" aria-hidden />
                  <span>Reports centre</span>
                </NavLink>

                {isSuperAdmin(user) && (
                  <>
                    <div className="layout-nav-divider" role="separator" aria-hidden />
                    <div className="layout-nav-section-label" aria-hidden>
                      Platform
                    </div>
                    <NavLink
                      to="/super-admin/organizations"
                      className={({ isActive }) =>
                        `layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`
                      }
                    >
                      <FiGlobe size={20} className="layout-nav-icon" aria-hidden />
                      <span>Organisations</span>
                    </NavLink>
                    <NavLink
                      to="/super-admin/admins"
                      className={({ isActive }) =>
                        `layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`
                      }
                    >
                      <FiShield size={20} className="layout-nav-icon" aria-hidden />
                      <span>Platform admins</span>
                    </NavLink>

                    <div className="layout-nav-divider" role="separator" aria-hidden />
                    <div className="layout-nav-section-label" aria-hidden>
                      Dev Tools
                    </div>
                    <NavLink
                      to="/dev/components"
                      className={({ isActive }) =>
                        `layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`
                      }
                    >
                      <FiLayers size={20} className="layout-nav-icon" aria-hidden />
                      <span>Components</span>
                    </NavLink>
                  </>
                )}

                <div className="layout-nav-divider" role="separator" aria-hidden />

                <div className="layout-nav-section-label" aria-hidden>
                  Account
                </div>
                <NavLink
                  to={settingsRoute}
                  className={({ isActive }) =>
                    `layout-nav-link ${settingsAreaActive || isActive ? 'layout-nav-link-active' : ''}`
                  }
                >
                  <FiSettings size={20} className="layout-nav-icon" aria-hidden />
                  <span>Settings</span>
                </NavLink>
                <NavLink to="/dev-notes" className={({ isActive }) =>
                  `layout-nav-link ${isActive ? 'layout-nav-link-active' : ''}`
                }>
                  <FiFileText size={20} className="layout-nav-icon" aria-hidden />
                  <span>Dev notes</span>
                </NavLink>
              </nav>
              <div className="layout-user">
                <span className="layout-user-name">
                  {user?.firstName} {user?.lastName}
                </span>
                <button type="button" onClick={handleLogout} className="layout-logout">
                  <FiLogOut size={18} className="layout-nav-icon" aria-hidden />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </aside>
        </>
      )}

      <main className={`layout-main ${showMobileTabs ? 'layout-main-with-tabs' : ''}`}>
        <Outlet />
      </main>

      {showMobileTabs && (
        <nav className="layout-mobile-tabs" aria-label="Primary">
          <NavLink to="/" end className={mobileTabClass}>
            <FiGrid size={20} aria-hidden />
            <span className="layout-mobile-tab-label">Dashboard</span>
          </NavLink>
          <NavLink
            to="/sites"
            className={() =>
              `layout-mobile-tab ${path.startsWith('/sites') ? 'layout-mobile-tab-active' : ''}`
            }
          >
            <FiMapPin size={20} aria-hidden />
            <span className="layout-mobile-tab-label">Sites</span>
          </NavLink>
          <NavLink to="/reports" className={mobileTabClass}>
            <FiBarChart2 size={20} aria-hidden />
            <span className="layout-mobile-tab-label">Reports</span>
          </NavLink>
          <NavLink
            to={settingsRoute}
            className={() =>
              `layout-mobile-tab ${settingsAreaActive ? 'layout-mobile-tab-active' : ''}`
            }
          >
            <FiSettings size={20} aria-hidden />
            <span className="layout-mobile-tab-label">Settings</span>
          </NavLink>
        </nav>
      )}
    </div>
  );
}

export default Layout;
